import { cn } from '@/lib/utils'

type ExtendedBorderLineProps = {
  borderName: string
  edge: 'top' | 'bottom'
  /** Which side of the column the line extends toward. */
  direction: 'left' | 'right'
  /** Animation sweep along the line. Defaults to rtl for left bleed, ltr for right bleed. */
  drawDirection?: 'ltr' | 'rtl'
  /** When set, line is confined to the cell and ends before the right edge (corner box width). */
  endInsetPx?: number
  /** When set, line is confined to the cell and starts after the left edge (corner box width). */
  startInsetPx?: number
  lineClassName?: string
  inView: boolean
  animateDraw?: boolean
  drawDelayMs?: number
  drawDurationMs?: number
}

/** Infinite horizontal rule — scaleX draw for scroll-triggered animation. */
export function ExtendedBorderLine({
  borderName,
  edge,
  direction,
  drawDirection,
  endInsetPx,
  startInsetPx,
  lineClassName = 'bg-white',
  inView,
  animateDraw = false,
  drawDelayMs = 0,
  drawDurationMs = 400,
}: ExtendedBorderLineProps) {
  const drawn = !animateDraw || inView
  const resolvedDrawDirection =
    drawDirection ?? (direction === 'left' ? 'rtl' : 'ltr')
  const isCellSpan = endInsetPx != null || startInsetPx != null
  const cellStartInset = startInsetPx ?? 0
  const cellEndInset = endInsetPx ?? 0

  return (
    <div
      aria-hidden
      data-border-name={borderName}
      className={cn(
        'pointer-events-none absolute z-10 h-px',
        isCellSpan ? undefined : 'w-[100vw]',
        edge === 'top' ? 'top-0' : 'bottom-0',
        isCellSpan
          ? undefined
          : direction === 'left'
            ? 'right-full'
            : 'left-full',
        resolvedDrawDirection === 'ltr' ? 'origin-left' : 'origin-right',
        lineClassName,
        animateDraw && 'transition-transform ease-in-out',
        drawn ? 'scale-x-100' : 'scale-x-0',
      )}
      style={{
        ...(isCellSpan
          ? {
              left: cellStartInset,
              width: `calc(100% - ${cellStartInset + cellEndInset}px)`,
            }
          : undefined),
        ...(animateDraw
          ? {
              transitionDuration: `${drawDurationMs}ms`,
              transitionDelay: `${drawDelayMs}ms`,
            }
          : undefined),
      }}
    />
  )
}
